import { useDisclosure } from '@chakra-ui/react';
import { useQuoteContext } from '../../../routes/Quotes/Quote/quoteContext';
import { formatUpliftSummaryLabel } from '../../../utils/quoteRenewalUplift.utils';
import { MButton, MSkeleton, MText } from '../../Monetize';
import { QuoteBasicInfoLine } from '../QuoteBasicInfoLine';
import { QuoteRenewalUpliftDrawer } from './QuoteRenewalUpliftDrawer';
import { useQuoteRenewalUplift } from './useQuoteRenewalUplift';

/**
 * Quote-level summary of renewal uplift with a drawer to configure it across
 * every offering at once. Renders nothing when the quote can neither carry an
 * uplift nor already has one.
 */
export const QuoteRenewalUpliftRow = () => {
  const { quote, eligibleOfferings, summary, isLoadingProductOfferings } =
    useQuoteRenewalUplift();
  const { isReadOnly } = useQuoteContext();
  const { open: isOpen, onOpen, onClose } = useDisclosure();

  // Existing configurations stay visible even if their offerings became
  // ineligible, but an all-onetime quote never shows a dead "Configure" row.
  if (!quote || (!eligibleOfferings.length && summary.status === 'none')) {
    return null;
  }

  if (isReadOnly && summary.status === 'none') {
    return (
      <QuoteBasicInfoLine title="Renewal Uplift">
        <MText data-testid="renewal-uplift-none">None</MText>
      </QuoteBasicInfoLine>
    );
  }

  // The summary's `isPartial` flag is derived from the eligible set, which
  // needs the product catalog — until it lands every quote looks fully covered,
  // so rendering now would show a flat "15%" and then flip it to
  // "15% (partial)". Nothing below can be decided without eligibility either.
  if (isLoadingProductOfferings) {
    return (
      <QuoteBasicInfoLine title="Renewal Uplift">
        <MSkeleton h="5" w="6rem" />
      </QuoteBasicInfoLine>
    );
  }

  const summaryLabel = formatUpliftSummaryLabel(summary);
  // A configuration that outlived its offering's eligibility still belongs in
  // the summary, but the drawer lists — and `buildUpliftSavePlan` reconciles —
  // only eligible offerings, so opening it would offer an empty, unsaveable
  // form.
  const hasNothingToEdit = !eligibleOfferings.length;

  return (
    <QuoteBasicInfoLine title="Renewal Uplift">
      <>
        {/* No dropdown arrow — unlike its neighbours this opens a drawer, not a menu */}
        <MButton
          variant="tertiary"
          size="sm"
          onClick={onOpen}
          isDisabled={hasNothingToEdit}
          mr="-3"
          aria-label={
            isReadOnly ? 'View renewal uplift' : 'Configure renewal uplift'
          }
          data-testid="renewal-uplift-trigger"
        >
          {summaryLabel}
        </MButton>
        {isOpen && <QuoteRenewalUpliftDrawer isOpen onClose={onClose} />}
      </>
    </QuoteBasicInfoLine>
  );
};
